import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { ReactNode } from 'react';
import { Divider } from 'semantic-ui-react';

import s from './popup-panel.scss';

export interface PopupPanelProps {
  /**
   * Content rendered beneath the header divider.
   */
  children: ReactNode;

  /**
   * Optional class name applied to the root element.
   */
  className?: string;

  /**
   * Optional content rendered on the trailing end of the heading row.
   */
  extra?: ReactNode;

  /**
   * Icon rendered before the title in the heading.
   */
  icon?: ReactNode;

  /**
   * Title text displayed in the heading.
   */
  title: string;
}

/**
 * Shared layout for click-triggered popup content. Renders a heading strip
 * (icon + title) above a divider, with arbitrary children below.
 *
 * Designed to be placed directly inside a Semantic UI `<Popup>` whose own
 * padding has been zeroed out so that `PopupPanel` can control the spacing.
 *
 * @example
 * ```tsx
 * <Popup className={s.popup} on="click" trigger={trigger}>
 *   <PopupPanel icon={<Icon name="dna" color="teal" />} title="Asset Details">
 *     ...content...
 *   </PopupPanel>
 * </Popup>
 * ```
 */
export const PopupPanel = ({
  children,
  className,
  extra,
  icon,
  title,
}: PopupPanelProps) => {
  useStyles(s);

  return (
    <div className={classNames(s.root, className)}>
      <div className={s.heading}>
        {icon}

        <span className={s.title}>{title}</span>

        {extra && <span className={s.extra}>{extra}</span>}
      </div>

      <Divider fitted />

      {children}
    </div>
  );
};

export default PopupPanel;
